import {EventEmitter} from '@angular/core';
import {WebsocketService} from '../websocket/websocket.service';
import {Observable} from 'rxjs';
import {RealtimeType} from './realtime-type.enum';
import {Utils} from '../../utils.class';
import {LocalStorage} from '../../storage.class';
import {RealtimeInterface} from './realtime-interface';

export abstract class RealtimeService<T extends RealtimeInterface> {

    websocketOpen = new EventEmitter<boolean>();

    private items: T[] = [];
    protected requestedDates: number[] = [];
    private requestedIds = new Map<(string | number), boolean>();
    private itemChanged = new EventEmitter<(number | string)[]>();
    private retryTimeoutPointer;
    protected listOnlyFilteredFrontend = false;
    private init = false;

    protected constructor(
        protected websocket: WebsocketService,
        protected type: RealtimeType,
        protected identifier = 'id'
    ) {
        if (!!LocalStorage.getUserToken()) {
            this.websocketInit();
        }
        this.websocket.websocketOpen.subscribe(isOpen => {
            this.websocketOpen.emit(isOpen);
            if (!isOpen) {
                this.retryWebsocketInit();
            }
        });
        this.websocket.loggedIn.subscribe(isLoggedIn => {
            if (isLoggedIn) {
                this.items = [];
                this.requestedDates = [];
                this.requestedIds = new Map<(string | number), boolean>();
                if (!this.init) {
                    this.websocketInit();
                }
            }
        });
    }

    public getFilteredList(fromDate: Date, toDate: Date, filterId?: any): Observable<T[]> {
        let newFromDate = Utils.setTime(new Date(fromDate), 0, 0);
        let newToDate = Utils.setTime(new Date(toDate), 0, 0);
        if (toDate.getTime() > newToDate.getTime()) {
            newToDate.setDate(newToDate.getDate() + 1);
        }

        return new Observable<T[]>((observer) => {
            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                observer.next(this.items.filter(item => this.itemInDaterange(item, fromDate, toDate)));
            });

            let loopDate = new Date(newFromDate);
            let allRequested = true;
            while (loopDate.getTime() < newToDate.getTime()) {
                if (this.requestedDates.indexOf(loopDate.getTime()) === -1) {
                    allRequested = false;
                }
                loopDate.setDate(loopDate.getDate() + 1);
            }
            if (allRequested || (this.requestedIds.has('list') && this.listOnlyFilteredFrontend)) {
                observer.next(this.items.filter(item => this.itemInDaterange(item, fromDate, toDate)));
            } else {
                let loopDate = new Date(newFromDate);
                let newStart = null;
                let newEnd = null;
                while (loopDate.getTime() < newToDate.getTime()) {
                    if (this.requestedDates.indexOf(loopDate.getTime()) === -1) {
                        if (!newStart) {
                            newStart = new Date(loopDate);
                        }
                        this.requestedDates.push(loopDate.getTime());
                        // This date should be included, to newEnd = today + 1
                        newEnd = new Date(loopDate);
                        newEnd.setDate(newEnd.getDate() + 1);
                    }
                    loopDate.setDate(loopDate.getDate() + 1);
                }
                this.websocket.doRequest(this.type, {
                    fromDate: newStart || newFromDate,
                    toDate: newEnd,
                    singleId: filterId
                });
            }
            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                }
            };
        });
    }

    private retryWebsocketInit() {
        this.init = false;
        if (this.retryTimeoutPointer) {
            clearTimeout(this.retryTimeoutPointer);
        }
        this.retryTimeoutPointer = setTimeout(() => {
            this.websocketInit();
        }, 1000);
    }

    public getList(urlParams: {} = {}, cacheKey: string = 'list'): Observable<T[]> {
        return new Observable<T[]>((observer) => {
            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                observer.next(this.items);
            });

            if (this.requestedIds.has(cacheKey)) {
                if (this.items) {
                    observer.next(this.items);
                }
            } else {
                this.websocket.doRequest(this.type, urlParams);
                this.requestedIds.set(cacheKey, true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                }
            };
        });
    }

    public getLatestItem(urlParams: null | {}): Observable<T> {
        return new Observable<T>((observer) => {
            const itemChangedSubscription = this.itemChanged.subscribe(() => {
                const item = this.items.length > 0 ? this.items[this.items.length - 1] : null;
                observer.next(item);
            });

            if (this.requestedIds.has('latest')) {
                if (this.items) {
                    const item = this.items.length > 0 ? this.items[this.items.length - 1] : null;
                    if (item) {
                        observer.next(item);
                    }
                }
            } else {
                this.websocket.doRequest(this.type, urlParams);
                this.requestedIds.set('latest', true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                }
            };
        });
    }

    public getSingle(id: number | string, idName: string = 'id', skipRequested = false): Observable<T> {
        return new Observable<T>((observer) => {
            const numericId = +id;

            const itemChangedSubscription = this.itemChanged.subscribe((ids) => {
                const item = this.items.find(p => +p[this.identifier] === numericId);
                if (item) {
                    observer.next(item);
                }
            });

            let item = null;
            if (this.items?.length) {
                item = this.items.find(p => +p[this.identifier] === numericId);
            }

            if (item) {
                observer.next(item);
            } else if (!this.requestedIds.has(numericId)) {
                this.websocket.doRequest(this.type, {[idName]: id});
                this.requestedIds.set(numericId, true);
            }

            return {
                unsubscribe(): void {
                    itemChangedSubscription.unsubscribe();
                }
            };
        });
    }

    private websocketInit() {
        this.init = true;
        this.websocket.getWebsocket<T>(this.type).subscribe((items: { data: T[] | T, reinit: boolean }) => {
                if (items?.data) {
                    const dataArray = Array.isArray(items.data) ? items.data : [items.data];

                    dataArray.filter(i => !!i).forEach(item => {
                        const currentItem = this.items.find(p => +p[this.identifier] === +item[this.identifier]);
                        if (item['deleted']) {
                            if (currentItem) {
                                this.items.splice(this.items.indexOf(currentItem), 1);
                            }
                        } else if (currentItem) {
                            const index = this.items.indexOf(currentItem);
                            this.items[index] = {...currentItem, ...item};
                        } else {
                            this.items.push(item);
                        }
                    });
                    const changedIds = dataArray.filter(i => !!i).map(i => isNaN(+i[this.identifier]) ? i[this.identifier] : +i[this.identifier]);
                    this.itemChanged.emit(changedIds);
                } else if (!!items?.reinit) {
                    this.retryWebsocketInit();
                }
            },
            () => this.retryWebsocketInit(),
            () => this.retryWebsocketInit()
        );
    }

    public abstract itemInDaterange(planning: T, fromDate: Date, toDate: Date);
}
